import { useCallback, useEffect, useState } from "react";
import { Search, RefreshCw } from "lucide-react";
import { AdminShell } from "../../components/admin/AdminShell";
import { OrdersTable } from "../../components/admin/OrdersTable";
import { OrderDetail } from "../../components/admin/OrderDetail";
import { adminFetch, STATUS_LABELS } from "../../lib/adminApi";

const AdminOrdersPage = () => (
  <AdminShell title="Orders"><OrdersView /></AdminShell>
);

const OrdersView = () => {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (status) params.set("status", status);
      const data = await adminFetch(`/api/orders?${params.toString()}`);
      setOrders(data);
      setSelected((s) => (s ? data.find((o) => o.order_id === s.order_id) || s : s));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [q, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const onSaved = (updated) => {
    setOrders((prev) => prev.map((o) => (o.order_id === updated.order_id ? updated : o)));
    setSelected(updated);
  };

  const counts = orders.reduce((m, o) => ({ ...m, [o.status]: (m[o.status] || 0) + 1 }), {});

  return (
    <div className={`grid grid-cols-1 gap-6 ${selected ? "lg:grid-cols-5" : ""}`}>
      <div className={selected ? "lg:col-span-3" : ""}>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <label className="relative flex-1">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order #, name, company, email, phone, tracking" className="w-full bg-[#141414] border border-white/20 pl-9 pr-3 py-2.5 text-white text-sm focus:border-signal-red focus:outline-none" data-testid="orders-search-input" />
          </label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-[#141414] border border-white/20 px-3 py-2.5 text-white text-sm focus:border-signal-red focus:outline-none" data-testid="orders-status-filter">
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}{counts[k] ? ` (${counts[k]})` : ""}</option>)}
          </select>
          <button type="button" onClick={load} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-white/20 text-gray-200 text-sm hover:border-signal-red" data-testid="orders-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
        </div>
        <p className="text-gray-500 text-xs mb-3" data-testid="orders-count">{loading ? "Loading…" : `${orders.length} order${orders.length === 1 ? "" : "s"}`}</p>
        {error && <p className="text-signal-red text-sm mb-3" data-testid="orders-error">{error}</p>}
        <OrdersTable orders={orders} onOpen={setSelected} selectedId={selected?.order_id} />
      </div>
      {selected && <div className="lg:col-span-2"><OrderDetail order={selected} onClose={() => setSelected(null)} onSaved={onSaved} /></div>}
    </div>
  );
};

export default AdminOrdersPage;
